/**
 * MuiThemeSync
 * Wraps children with MUI ThemeProvider that stays in sync with the Tailwind dark/light mode.
 * Colors are mapped to match the project's Tailwind palette so MUI components look native.
 */

import { useMemo } from 'react'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { useTheme } from '@contexts/ThemeContext'

export default function MuiThemeSync({ children }) {
  const { isDark } = useTheme()

  const muiTheme = useMemo(() => createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: { main: '#7c3aed', light: '#a78bfa', dark: '#5b21b6' },       // violet-600/400/800
      secondary: { main: '#6366f1', light: '#818cf8', dark: '#4338ca' },      // indigo-500/400/700
      success: { main: '#10b981', light: '#34d399', dark: '#059669' },        // emerald-500/400/600
      error: { main: '#ef4444', light: '#f87171', dark: '#dc2626' },          // red-500/400/600
      warning: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706' },        // amber-500/400/600
      info: { main: '#3b82f6', light: '#60a5fa', dark: '#2563eb' },           // blue-500/400/600
      ...(isDark ? {
        background: { default: '#111827', paper: '#1f2937' },                 // gray-900/800
        text: { primary: '#f9fafb', secondary: '#9ca3af' },                   // gray-50/400
        divider: '#374151'                                                     // gray-700
      } : {
        background: { default: '#f9fafb', paper: '#ffffff' },                 // gray-50/white
        text: { primary: '#111827', secondary: '#6b7280' },                   // gray-900/500
        divider: '#e5e7eb'                                                     // gray-200
      })
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif'
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: { body: { backgroundColor: 'transparent' } }
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundImage: 'none',
            ...(theme.palette.mode === 'dark' && {
              backgroundColor: '#1f2937',
              borderColor: '#374151'
            })
          })
        }
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500 }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.palette.mode === 'dark' ? '#374151' : '#e5e7eb'
          })
        }
      }
    }
  }), [isDark])

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline enableColorScheme />
      {children}
    </ThemeProvider>
  )
}
